/**
 * The ICD-10 sex-incompatibility gate.
 *
 * Pure logic + a single chat-state applicator. Strips (or mirror-substitutes) diagnosis
 * codes that are anatomically impossible for the patient's recorded sex, BEFORE they
 * render or sync to the EMR. Reference data + the wrong-sex/right-sex rules live in
 * src/utils/icd10SexEdits.js. Tested in src/utils/__tests__/icd10SexGate.test.js.
 *
 * DESIGN
 *   - Single chokepoint: applyIcd10SexGate() runs on the freshly-loaded chat in
 *     refreshChat() (src/hooks/useChats.js), gating local:billableCodesICD10 and its
 *     ...Initial snapshot. Because every downstream consumer (note render, {{ }} EHR
 *     templating, the raw [[ note.contextVariables[...] ]] JS path, sanitizeCodes magic
 *     paste, PDF, audit packet) reads that same variable, gating it once makes all of
 *     them clean. A thin defensive re-gate runs at EHR sync entry.
 *   - Fail-open: when the patient's sex is unknown / "Other" / not clearly M or F we
 *     touch nothing. CMS deactivated the MCE sex edits in 2024 precisely because
 *     sex-discordant codes are sometimes legitimate (transgender, congenital, history-of);
 *     we never strip when we cannot be certain.
 *   - Auditable: every removal/substitution is recorded on
 *     local:billableCodesICD10SexFiltered so a silently-dropped code is always recoverable.
 */

import {
  classifyCodeForSex,
  normalizeIcdForMatch,
  isFemaleOnlyCode,
  isMaleOnlyCode,
  GATE_MODE,
  MCE_EDITION,
} from "./icd10SexEdits";

/**
 * Normalize the messy `chat.user.custom.gender` field to "M" | "F" | null.
 * Mirrors mapGenderToHL7 (src/utils/hl7Utils.js) but returns null (fail-open) instead
 * of "U" for anything that isn't unambiguously male or female.
 */
export function normalizeSex(gender) {
  if (!gender) return null;
  const lower = String(gender).toLowerCase().trim();
  if (lower === "male" || lower === "m") return "M";
  if (lower === "female" || lower === "f") return "F";
  return null;
}

// Extract the bare code from a "CODE - description" string (or dict key), mirroring the
// HTML-strip + " - " split that sanitizeCodes/normalizList already use.
function codeOfStringItem(str) {
  const cleaned = String(str == null ? "" : str).replace(/<[^>]*>/g, "");
  const i = cleaned.indexOf(" - ");
  return (i >= 0 ? cleaned.slice(0, i) : cleaned).trim();
}

function dedupeBy(arr, keyFn) {
  const seen = new Set();
  const out = [];
  for (const item of arr) {
    const k = keyFn(item);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(item);
  }
  return out;
}

/**
 * Filter a billable-codes value against a patient sex, preserving the input's shape.
 * Accepts the three backend shapes the rest of the app already handles:
 *   - Array of objects   [{ code, description, alternatives?, ... }]
 *   - Array of strings   ["N95.1 - Menopausal states", ...]
 *   - Dict keyed by code "{ 'N95.1 - Menopausal states': { evidence, ... } }"
 *
 * Returns { kept, removed, substituted }:
 *   - kept        — same shape as input, with conflicts removed/substituted/flagged
 *   - removed     — [{ code, sex, flaggedOnly? }] codes stripped (or flagged in warn mode)
 *   - substituted — [{ from, to, sex }] codes swapped for their sex-appropriate counterpart
 *
 * Fails open: unknown/ambiguous sex returns the input untouched. Never throws on shape.
 */
export function filterSexIncompatibleCodes(value, sex) {
  const s = normalizeSex(sex);
  const removed = [];
  const substituted = [];

  if (value == null) return { kept: value, removed, substituted };
  if (s !== "M" && s !== "F") return { kept: value, removed, substituted };

  const processAlternatives = (alts) => {
    const r = filterSexIncompatibleCodes(alts, s);
    removed.push(...r.removed);
    substituted.push(...r.substituted);
    return r.kept;
  };

  if (Array.isArray(value)) {
    const kept = [];
    for (const item of value) {
      if (typeof item === "string") {
        const code = codeOfStringItem(item);
        const { conflict, mirror } = classifyCodeForSex(code, s);
        if (!conflict) {
          kept.push(item);
        } else if (mirror && GATE_MODE === "silent") {
          kept.push(mirror.code + " - " + mirror.description);
          substituted.push({ from: normalizeIcdForMatch(code), to: mirror.code, sex: s });
        } else if (GATE_MODE === "warn") {
          kept.push(item);
          removed.push({ code: normalizeIcdForMatch(code), sex: s, flaggedOnly: true });
        } else {
          removed.push({ code: normalizeIcdForMatch(code), sex: s });
        }
        continue;
      }

      if (item && typeof item === "object") {
        const { conflict, mirror } = classifyCodeForSex(item.code, s);
        let next = item;
        if (conflict) {
          if (mirror && GATE_MODE === "silent") {
            next = { ...item, code: mirror.code, description: mirror.description };
            substituted.push({ from: normalizeIcdForMatch(item.code), to: mirror.code, sex: s });
          } else if (GATE_MODE === "warn") {
            next = { ...item, _sexConflict: true };
            removed.push({ code: normalizeIcdForMatch(item.code), sex: s, flaggedOnly: true });
          } else {
            removed.push({ code: normalizeIcdForMatch(item.code), sex: s });
            continue; // silent drop
          }
        }
        if (next && next.alternatives != null) {
          next = { ...next, alternatives: processAlternatives(next.alternatives) };
        }
        kept.push(next);
        continue;
      }

      kept.push(item);
    }
    return { kept, removed, substituted };
  }

  if (typeof value === "object") {
    const kept = {};
    for (const key of Object.keys(value)) {
      const code = codeOfStringItem(key);
      const { conflict, mirror } = classifyCodeForSex(code, s);
      let val = value[key];
      if (val && typeof val === "object" && val.alternatives != null) {
        val = { ...val, alternatives: processAlternatives(val.alternatives) };
      }
      if (!conflict) {
        kept[key] = val;
      } else if (mirror && GATE_MODE === "silent") {
        kept[mirror.code + " - " + mirror.description] = val;
        substituted.push({ from: normalizeIcdForMatch(code), to: mirror.code, sex: s });
      } else if (GATE_MODE === "warn") {
        // Tag the value like the array-object path does, so a future flip to
        // warn mode gives the UI a flag to render regardless of list shape.
        kept[key] = val && typeof val === "object" && !Array.isArray(val) ? { ...val, _sexConflict: true } : val;
        removed.push({ code: normalizeIcdForMatch(code), sex: s, flaggedOnly: true });
      } else {
        removed.push({ code: normalizeIcdForMatch(code), sex: s });
      }
    }
    return { kept, removed, substituted };
  }

  return { kept: value, removed, substituted };
}

/**
 * True if a billable-codes value contains ANY sex-specific code (female-only OR male-only),
 * regardless of patient sex. Used to decide whether to surface the "set patient sex" prompt
 * on a chart that has no sex on file — there's no point asking for sex unless a sex-specific
 * code is actually present. Accepts the same three shapes as filterSexIncompatibleCodes.
 */
export function hasSexSpecificCodes(value) {
  if (value == null) return false;
  const codes = [];
  if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item === "string") codes.push(codeOfStringItem(item));
      else if (item && typeof item === "object") codes.push(item.code);
    }
  } else if (typeof value === "object") {
    for (const key of Object.keys(value)) codes.push(codeOfStringItem(key));
  }
  return codes.some((c) => isFemaleOnlyCode(c) || isMaleOnlyCode(c));
}

/**
 * Apply the gate to a freshly-loaded chat object IN PLACE and return it. Gates the
 * working billable-codes list and its pre-validation snapshot, and records an auditable
 * trail of everything removed/substituted.
 *
 * Idempotent and refresh-safe: refreshChat runs repeatedly and the backend may keep
 * re-sending the same wrong-sex code, so the audit record is MERGED + deduped rather than
 * overwritten — a later clean pass never erases an earlier removal record. Never throws;
 * on any unexpected error it degrades to passing the chat through ungated.
 */
export function applyIcd10SexGate(chatData) {
  try {
    if (!chatData || !chatData.contextVariables) return chatData;
    const cv = chatData.contextVariables;
    const sex = normalizeSex(chatData.user && chatData.user.custom && chatData.user.custom.gender);
    if (sex !== "M" && sex !== "F") return chatData; // fail-open

    const allRemoved = [];
    const allSubstituted = [];

    // `source` tags audit entries from the pre-validation snapshot: since the
    // backend started persisting billableCodesICD10Initial after empty/filtered
    // passes (lucasai-backend#268), the snapshot can hold codes that never
    // reached the note — without the tag those read as removals FROM the note.
    // The working list is gated first, so on code-level dedupe an untagged
    // (note-sourced) record wins over a snapshot-sourced one.
    const gateVar = (key, source) => {
      if (cv[key] == null) return;
      const { kept, removed, substituted } = filterSexIncompatibleCodes(cv[key], sex);
      if (removed.length || substituted.length) {
        cv[key] = kept;
        allRemoved.push(...(source ? removed.map((r) => ({ ...r, source })) : removed));
        allSubstituted.push(...(source ? substituted.map((s) => ({ ...s, source })) : substituted));
      }
    };

    gateVar("local:billableCodesICD10");
    gateVar("local:billableCodesICD10Initial", "initial");

    if (allRemoved.length || allSubstituted.length) {
      const prior = cv["local:billableCodesICD10SexFiltered"] || { removed: [], substituted: [] };
      cv["local:billableCodesICD10SexFiltered"] = {
        mceEdition: MCE_EDITION,
        sex,
        removed: dedupeBy([...(prior.removed || []), ...allRemoved], (r) => r.code),
        substituted: dedupeBy([...(prior.substituted || []), ...allSubstituted], (r) => r.from + ">" + r.to),
      };
    }
    return chatData;
  } catch (e) {
    console.error("icd10SexGate: failed to apply, passing chat through ungated", e);
    return chatData;
  }
}
